<template>
  <div class="checkin">
    <div class="wrapper p-md-4 p-sm-5 m-0 m-md-4">
      <!-- Topbar -->
      <div class="d-flex justify-content-between align-items-center mb-3 px-3 pt-3">
        <div>
          <!-- Left Nav -->
           <button v-if="action != 'search_booking'" class="btn btn-custom-primary" @click="back">
            <span alt="Back" title="Back" class="fa fa-arrow-left"></span>
           </button>
        </div>
        <div class="d-flex">
          <!-- Right Nav -->
           <nav class="nav-menu">
            <ul class="mb-0 pl-0">
              <li class="dropdown">
                <language-button-component :facility_id="facility.id" @select-lang="selectLang" :checkin="true" />
              </li>
            </ul>
          </nav>
          <div class="ml-2">
            <a href="#" @click="$bvModal.show('modal-guide')" class="btn btn-info text-white">
              {{ $t("visit-information-form.help") }}
              <i class="fa fa-question-circle"></i>
            </a>
            <guide-component />
          </div>
        </div>
      </div>

      <!-- Body -->
      <!-- Step 1aa: Search booking by phone number -->
      <div v-if="action == 'search_booking'" class="p-3">
        <p>{{ $t('checkin_by_phone.instruction') }}</p> 
        <form @submit.prevent="submitByPhone">
          <div class="form-group">
            <label class="phone-info">
              {{ $t('checkin_by_phone.label_checkin_date') }}
            </label>
            <input class="form-control" type="text" :value="checkin_date" readonly />
          </div>
          <div class="form-group">
            <label class="phone-info">
              {{ $t('checkin_by_phone.label_phone_number') }}
              <i class="fas fa-info-circle text-primary" v-b-tooltip.hover :title="$t('pre_checkin_bmatch.wo_phone_tips')" />
            </label>
            <vue-tel-input 
              class="mt-2"
              mode="international" 
              :validCharactersOnly="true" 
              @country-changed="phoneCountryChange" 
              @input="phoneInput"
              v-model="phone_raw"  
              :placeholder="$t('pre_checkin_bmatch.wo_phone_placeholder')" 
              required
            />
          </div>
          <div class="text-right">
            <button type="submit" class="btn btn-form-control btn-custom-primary" :disabled="!phone_number">
              {{ $t("visit-information-form.confirm") }}
            </button>
          </div>
          <div v-if="info_msg" class="alert alert-info mt-3 mb-0">
            {{ info_msg }}
          </div>
          <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
            {{ error_msg }}
          </div>
        </form>
      </div>

      <!-- Step 1b: Search by name if multiple bookings were found -->
      <div v-if="action == 'search_name'" class="p-3">
        <p>{{ $t('checkin_by_phone.instruction_guest_name') }}</p>
        <form @submit.prevent="submitByGuestName">
          <div class="form-group search-suggestion-box">
            <label class="phone-info">
              {{ $t('checkin_by_phone.label_guest_name') }}
            </label>
            <input class="form-control search_field" type="text" v-model="guest_name" @input="searchNames" />
          </div>
          <div class="text-right">
            <button type="submit" class="btn btn-form-control btn-custom-primary" :disabled="!phone_number">
              {{ $t("visit-information-form.confirm") }}
            </button>
          </div>
          <div v-if="info_msg" class="alert alert-info mt-3 mb-0">
            {{ info_msg }}
          </div>
          <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
            {{ error_msg }}
          </div>
        </form>
      </div>

      <!-- Step 2: Display localized filter for guest checkin -->
      <div v-if="action == 'rsv_to_ci_select_filter'" class="p-3">
        <div class="info mb-4">
          <h6 v-html="custom_text_setting.title"></h6>
          <p v-html="custom_text_setting.header"></p>
        </div>
        <localized-filter :localized_filters="localized_filters" @select-filter="selectFilter" />
      </div>

      <!-- Step 3: Display checkin form and or confirm details. Confirmation UI vary between rsv_to_ci and pci_to_ci -->
      <div v-if="ci_form_actions.includes(action)" class="p-3">
        <form @submit.prevent="submitForm">
          <div class="info mb-4">
            <template v-if="['rsv_to_ci_form_input', 'rsv_to_ci_form_confirm'].includes(action)">
              <h6 v-html="custom_text_setting.title"></h6>
              <p v-html="custom_text_setting.header"></p>
            </template>
            <template v-if="['pci_to_ci_form_input', 'pci_to_ci_form_confirm'].includes(action)">
              <h6>{{ facility_name }}</h6>
              <p>{{ $t('checkin_by_phone.instruction_pci_to_ci') }}</p>
            </template>
          </div>
          <div>
            <pre-checkin-booking-details
              :booking_details="booking_details"
              :facility="facility"
              :questions="questions"
              :noQuestions="false"
              :currency="currency"
              ref="bookingDetailsComponent"
              @populate="populate($event)"
            />
          </div>

          <!-- Questionnaire & Answers -->
          <template v-if="['rsv_to_ci_form_input', 'rsv_to_ci_form_confirm'].includes(action)">
            <p style="font-weight: bold">{{ facility_name }}</p>
            <hr />
            <template v-for="(c, index) in questions">
              <default-component
                v-if="!specials.includes(c.system_name)"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :canBeCopy="canBeCopy"
                :form_choice_temp.sync="form_choice_temp"
                @add-guest="guestCount++"
                @remove-guest="guestCount--"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                ref="defaultComponent"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <nationality-component 
                v-if="c.system_name == 'nationality'" 
                :c="c" :confirm.sync="confirm" 
                :checkin_form_type="checkin_form_type" 
                :reservation_no="reservation_no" 
                :visitor_forms.sync="visitor_forms" 
                :multiple="false" 
                :multiple_data="multiple_data"
                :bd_index="null" 
                :key="`q_${index}`" 
              />

              <email-component 
                v-if="c.system_name == 'email'" 
                :c="c" 
                :confirm.sync="confirm" 
                :user.sync="user" 
                :checkin_form_type="checkin_form_type" 
                :reservation_no="reservation_no" 
                :visitor.sync="visitor" 
                :multiple="false" 
                :multiple_data="multiple_data" 
                :bd_index="null" 
                :key="`q_${index}`" 
                @email-exists="email_exists = $event" 
              />

              <age-component
                v-if="c.system_name == 'age'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <gender-component
                v-if="c.system_name == 'gender'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <dob-component
                v-if="c.system_name == 'dob'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :form_language.sync="form_language"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <body-temperature-component
                v-if="c.system_name == 'body_temperature'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <core-choices-component
                v-if="yesNo.includes(c.system_name)"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :choice="'yes_no'"
                :canBeCopy="canBeCopy"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <core-choices-component
                v-if="choices2.includes(c.system_name)"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :choice="'choices2'"
                :canBeCopy="canBeCopy"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />

              <relationship-component
                v-if="c.system_name == 'relationship_with_main_visitor'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :key="`q_${index}`"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />
            </template>

            <!-- Meal component -->
            <template>
              <meal-component
                :facility="facility"
                :companions.sync="companions"
                :mealDetails="mealDetails"
                @mealSelection="mealSelection"
                :confirm.sync="confirm"
                ref="mealComponent"
                :hasAvailableTables.sync="hasAvailableTables"
              />
            </template>

            <hr />

            <!-- For data aggregation (Camera Integration) -->
            <template v-for="(c, index) in questions">
              <gov-id-component
                v-if="c.system_name == 'gov_id'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :questions="questions"
                :images.sync="images"
                :guestCount.sync="guestCount"
                :key="`qg_${index}`"
                @image-captured="setVisitorImage($event)"
                @save-images="saveImages"
                @retake-image="retakeImage($event)"
                @retake-gov-id="retakeGovId($event)"
                @removeCompanionInMainForm="handleRemoveCompanionUsingGovId"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />
              <vaccine-card-component
                v-if="c.system_name == 'vaccine_card'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :questions="questions"
                :images="vaccine_card_images"
                :guestCount="guestCount"
                :key="`qv_${index}`"
                @image-captured="setVisitorVaccineCardImages($event)"
                @save-images="saveImages($event)"
                @retake-image="retakeVisitorVaccineCardImage($event)"
                @retake-gov-id="retakeGovId($event)"
                @add-guest="guestCount++"
                @remove-guest="guestCount--"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
                :fixGuestCount="guestCompanions"
                :fixGuestSetting="guestCountSetting"
              />
              <hr v-if="c.system_name == 'signature'" :key="`qg_${index}`" />
              <signature-component
                v-if="c.system_name == 'signature'"
                :c="c"
                :confirm.sync="confirm"
                :visitor_forms.sync="visitor_forms"
                :companions.sync="companions"
                :different="different"
                :questions="questions"
                :multiple="false"
                :multiple_data="multiple_data"
                :bd_index="null"
                :filled_in_by_other="null"
                :key="`qs_${index}`"
                @clear="clearSigError"
                @set="clearSigError"
                @written="signature_written = true"
                :checkin_form_type="checkin_form_type"
                :reservation_no="reservation_no"
              />
            </template>

            <!-- Confirmation ui for companions -->
            <template v-if="confirm">
              <div v-for="(c, i) in companions" :key="`co_${i}`">
                <div class="form-group-row">
                  <div class="col-lg-12 alert alert-secondary py-2">
                    <div class="d-flex justify-content-between">
                      <div>
                        {{ $t("visit-information-form.companion") }}
                        {{ i + 1 }}
                      </div>
                      <a href="javascript:void(0)" v-b-toggle="`companion-${i}`" class="text-dark"><i class="fa fa-chevron-down"></i></a>
                    </div>
                  </div>
                </div>
                <b-collapse :id="`companion-${i}`" visible class="mt-2"">
                  <template v-for="(qc, index) in questions">
                    <div 
                      v-if="!sameToAll.includes(qc.system_name) && qc.system_name != 'gov_id' && qc.system_name != 'vaccine_card' && qc.is_core == 1" :key="`qc_${index}`"
                      class="form-group row" 
                    >   
                      <div class="col-sm-5">
                        <label class="pl-1 col-auto col-form-label">
                          {{ qc.label }}<small v-if="qc.required" class="text-danger">*</small>
                        </label>
                      </div>
                      <div class="col-auto companion-details" v-if="!specials.includes(qc.system_name)">
                        <p class="pl-1 text-break my-auto">{{ c.other_forms[qc.id] }}</p>
                      </div>
                      <div class="col-auto companion-details" v-if="qc.system_name == 'age'">
                        <p class="pl-1 my-auto">{{ c.other_forms[qc.id] }}</p>
                      </div>
                      <div class="col-auto companion-details" v-if="qc.system_name == 'gender'">
                        <p class="pl-1 my-auto">
                          {{ getChoice("genders", c.other_forms[qc.id]) }}
                        </p>
                      </div>
                      <div class="col-auto companion-details" v-if="qc.system_name == 'dob'">
                        <p class="pl-1 my-auto">{{ formatDate(c.other_forms[qc.id]) }}</p>
                      </div>
                      <div class="col-auto companion-details" v-if="qc.system_name == 'body_temperature'">
                        <p class="pl-1 my-auto">{{ c.other_forms[qc.id] }}</p>
                      </div>
                      <div class="col-auto companion-details" v-if="yesNo.includes(qc.system_name)">
                        <p class="pl-1 my-auto">{{ getChoice("yes_no", c.other_forms[qc.id]) }}</p>
                      </div>
                      <div class="col-auto companion-details" v-if="choices2.includes(qc.system_name)">
                        <p class="pl-1 my-auto">
                          {{ getChoice("choices2", c.other_forms[qc.id]) }}
                        </p>
                      </div>
                      <div class="col-auto companion-details" v-if="qc.system_name == 'relationship_with_main_visitor'">
                        <p class="pl-1 my-auto">
                          {{ getChoice("relationships", c.other_forms[qc.id]) }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <template v-for="(qc, index) in questions">
                    <div class="form-group row" v-if="qc.system_name == 'gov_id'" :key="`qc2_${index}`">
                      <label class="col-auto col-form-label"> {{ qc.label }}<small v-if="qc.required" class="text-danger">*</small> </label>
                      <div class="col-auto">
                        <p v-if="typy(c.other_forms[qc.id], 'dataUrl').isDefined">
                          <img class="img-thumbnail" :src="c.other_forms[qc.id].dataUrl" />
                        </p>
                      </div>
                    </div>
                    <div class="form-group row" v-if="qc.system_name == 'vaccine_card'" :key="`qc_${index}`">
                      <label class="col-auto col-form-label"> {{ qc.label }}<small v-if="qc.required" class="text-danger">*</small> </label>
                      <div class="col-auto">
                        <p v-if="typy(c.other_forms[qc.id], 'dataUrl').isDefined">
                          <img class="img-thumbnail" :src="c.other_forms[qc.id].dataUrl" />
                        </p>
                      </div>
                    </div>
                  </template>
                </b-collapse>
                <hr />
              </div>
            </template>

            <!-- Payment UI -->
            <div v-if="!guestIsPaid && showPaymentGateway" class="mb-3">
              <GatewayPayment
                ref="gatewayPayment" 
                :facility="facility" 
                :booking_details="booking_details" 
                :confirm="confirm" 
                :currency="currency" 
                :touchdown="true" 
                page="checkin"
                gateway="checkin"
              />
            </div>

            <!-- Terms And Conditions -->
            <template v-if="confirm" >
              <div v-for="(value, key) in questions" class="row">
                <div class="col-lg-12 mb-4 text-center" v-if="value.system_name == 'terms_conditions'" :key="`qk${key}`">
                  <div class="mt-3 mb-2">
                    <terms-component :data="value.metadata.content"></terms-component>
                  </div>
                  <div class="form-check">
                    <input 
                      v-model="visitor_forms[value.id]" :true-value="$t('visit-information-form.yes')"
                      class="form-check-input" type="checkbox" value='' id="terms_conditions_agree"
                      @input="setAgreementStatus"
                    />
                    <label class="form-check-label text-decoration-none" for="terms_conditions_agree">
                      {{ $t("visit-information-form.agreed_terms_and_condition") }}
                    </label>
                  </div>
                  <hr />
                </div>
              </div>
            </template>
          </template>

          <!-- From PCI to CI Answers Confirmation -->
          <div v-if="(['pci_to_ci_form_input','pci_to_ci_form_confirm'].includes(action)) && !guestIsPaid && showPaymentGateway" class="mb-3">
            <GatewayPayment
              ref="gatewayPayment" 
              :facility="facility" 
              :booking_details="booking_details" 
              :confirm="action == 'pci_to_ci_form_confirm'" 
              :currency="currency" 
              :touchdown="true" 
              page="checkin"
              gateway="checkin"
            />
          </div>

          <!-- Validation Messages -->
          <div 
            v-if="!gov_id_exist || !gov_id_companion.exist || !vaccine_card_exist || !vaccine_card_companion.exist || !signature_exist"
            class="row mb-4" 
          >
            <div class="col-lg-12 text-center">
              <small style="font-size: 15px" class="form-text text-danger d-block">
                <span class="d-block" v-if="!gov_id_exist || !gov_id_companion.exist">
                  {{ $t("visit-information-form.passport_validation") }}
                </span>
                <span class="d-block" v-if="!vaccine_card_exist || !vaccine_card_companion.exist">
                  {{ $t("visit-information-form.vaccine_card_validation") }}
                </span>
                <span class="d-block" v-if="!signature_exist && !signature_written">
                  {{ $t("visit-information-form.signature_validation_1") }}
                </span>
                <span class="d-block" v-if="!signature_exist && signature_written">
                  {{ $t("visit-information-form.signature_validation_2") }}
                </span>
              </small>
            </div>
          </div>

        <!-- MRS: no available tables checker error -->
          <div class="row">
            <div class="col-lg-12" align="center">
                <div class="alert alert-danger" role="alert" v-show="showMealError">
                  {{ $t('visit-information-form.meal_reservation.no_tables_error') }}
                </div>
            </div>
          </div>
          
          <!-- Form Buttons -->
          <div class="row">
            <div class="col text-center">
              <button type="button" class="btn btn-form-control btn-outline-danger btn-w-lg mb-2 mx-1" @click="back">
                {{ $t("visit-information-form.back") }}
              </button>
              <button type="submit" class="btn btn-form-control btn-custom-primary btn-w-lg mb-2 mx-1" :disabled="confirm && !agree_to_terms">
                {{ this.confirm ? $t("visit-information-form.submit") : $t("visit-information-form.confirm") }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <b-modal id="processing-modal" hide-header hide-footer no-close-on-esc no-close-on-backdrop centered>
      <div class="text-center">
        <i class="fas fa-spinner fa-spin processing-icon"></i>
        <div class="processing-text">
            {{ $t("visit-information-form.processing-wait") }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.checkin {
  font-family: "Poppins", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #dff5ff;

  .wrapper {
    margin: 3rem 0;
    background: #fff;
    height: auto;
    width: 100%;
    max-width: 1144px;
    border-radius: 10px;
    -webkit-box-shadow: 3px 10px 6px #65656552; /* Safari 3-4, iOS 4.0.2 - 4.2, Android 2.3+ */
    -moz-box-shadow: 3px 10px 6px #65656552; /* Firefox 3.5 - 3.6 */
    box-shadow: 3px 10px 6px #65656552; /* Opera 10.5, IE 9, Firefox 4+, Chrome 6+, iOS 5 */

    .info {
      h6 {
        color: #1a5dab;
        font-size: 16px;
      }
      p {
        font-size: 14px;
      }
    }
  }
}
input[type=text] {
  text-align: center;
}
.btn-form-control {
    min-width: 156px;
    border-radius: 20px;
}
.btn-custom-primary {
  color: white;
  border: 1px solid #1aa8de !important;
  background-color: #1aa8de !important;
  &:not([disabled]):hover {
    border: 1px solid #0b89bb !important;
    background-color: #0b89bb !important;
  }
}
.btn-info {
  min-width: 100px;
}
.processing-icon {
  font-size: 45px;
  margin-bottom: 30px;
}
.processing-text, .leave-warning-text {
  font-size: 14px;
  font-family: "Poppins", sans-serif;
}
#processing-modal___BV_modal_outer_ {
  z-index: 10000 !important;
}

/* Search Suggestion Box */
.search-suggestion-box {
  position: relative;
  .suggestion-list {
    display: none;
    position: absolute;
    top: 71px;
    left: 0px;
    background-color: white;
    width: 100%;
    border: 1px solid #ced4da;
    padding-left: 0px;
    li.suggestion {
      display: block;
      width: 100%;
      padding: 5px;
      cursor: pointer;
      opacity: 0.8;
      &:hover {
        background-color: whitesmoke;
        opacity: 1;
      }
    }
  }
}
.search_field:focus {
  &~ul.suggestion-list {
    display: block;
  }
}

@media(max-width: 768px) {
  .checkin {
    .wrapper {
      min-height: 100vh;
      border-radius: 0px;
    }
  }
}
</style>

<script>
import { mapActions, mapState } from "vuex";
import moment from 'moment-timezone';
import t from "typy";
import LanguageButtonComponent from "../../dashboard/components/language-button.component.vue";
import LocalizedFilter from "./../components/localizedFilter.vue";
import GatewayPayment from "./../components/gatewayPayment.vue";
import GuideComponent from "./../../../components/guide.component.vue";
import TermsComponent from "./../../../components/terms.component.vue";
import preCheckinBookingDetails from "./../components/preCheckinBookingDetails.vue";
// import SelectOptionModal from "./../components/selectOptionModal.vue";
import AgeComponent from "./form-components/AgeComponent.vue";
import BodyTemperatureComponent from "./form-components/BodyTemperatureComponent.vue";
import CoreChoicesComponent from "./form-components/CoreChoicesComponent.vue";
import DefaultComponent from "./form-components/DefaultComponent.vue";
import DobComponent from "./form-components/DobComponent.vue";
import EmailComponent from "./form-components/EmailComponent.vue";
import GenderComponent from "./form-components/GenderComponent.vue";
import GovIdComponent from "./form-components/GovIdComponent.vue";
// import MultipleRoomComponent from "./form-components/MultipleRoomComponent.vue";
import NationalityComponent from "./form-components/NationalityComponent.vue";
import SignatureComponent from "./form-components/SignatureComponent.vue";
import VaccineCardComponent from "./form-components/VaccineCardComponent.vue";
import RelationshipComponent from "./form-components/companion/RelationshipComponent.vue";
import MealComponent from "./form-components/MealComponent.vue";

import { eventBus } from "@/main";

import {
   main_form_methods, 
  //  payment_gateway,
  //  payment_status, 
  //  sys_choices
} from "../../../utils/mockup";

const {
  getChoice,
  formatDate,
  loadAgeDropdown,
  // checkAddCompanions,
  // isRsvForm,
  // getLength,
  // isFixGuestEnabled
} = main_form_methods;
const specials = [
  "gender",
  "email",
  "nationality",
  "age",
  "dob",
  "gov_id",
  "internation_travel",
  "domestic_travel",
  "foreign_travel",
  "close_contact_corona_patient",
  "relationship_with_main_visitor",
  "body_temperature",
  "coughing",
  "doctor_certificate",
  "pass_affection",
  "terms_conditions",
  "signature",
  "vaccine_card"
];
const different = [
  "full_name",
  "first_name",
  "last_name",
  "age",
  "dob",
  "gender",
  "occupancy",
  "gov_id_number",
  "body_temperature",
  "address",
  "coughing",
  "doctor_certificate",
  "pass_affection",
  "prev_destination",
  "next_destination",
  "transportation",
  "internation_travel",
  "domestic_travel",
  "signature",
  "vaccine_card"
];
const canBeCopy = [
  "address",
  "coughing",
  "doctor_certificate",
  "pass_affection",
  "prev_destination",
  "next_destination",
  "transportation",
  "internation_travel",
  "domestic_travel",
  "foreign_travel",
  "close_contact_corona_patient"
];
const sameToAll = [
  "phone",
  "nationality",
  "email",
  "reservation_no",
  'terms_conditions',
  'signature'
];
const yesNo = [
  'coughing',
  'doctor_certificate',
  'pass_affection',
  'internation_travel',
  'domestic_travel',
];
const choices2 = [
  'foreign_travel',
  'close_contact_corona_patient'
];

// Stop checkout conditions
const checkedOutStatusIds = [3, 10, 11, 15, 16, 17];

export default {
  components: {
    LanguageButtonComponent,
    LocalizedFilter,
    preCheckinBookingDetails,
    DefaultComponent,
    GuideComponent,
    GatewayPayment,
    NationalityComponent,
    EmailComponent,
    AgeComponent,
    GenderComponent,
    DobComponent,
    BodyTemperatureComponent,
    CoreChoicesComponent,
    RelationshipComponent,
    SignatureComponent,
    GovIdComponent,
    TermsComponent,
    VaccineCardComponent,
    MealComponent,
  },
  data() {
    return {
      step: 1, // steps: 1,2,3
      action: 'search_booking',
      ci_form_actions: [
        'rsv_to_ci_form_input',
        'rsv_to_ci_form_confirm',
        'pci_to_ci_form_input',
        'pci_to_ci_form_confirm',
      ],
      ci_form_confirm_actions: [
        'rsv_to_ci_form_confirm',
        'pci_to_ci_form_confirm',
      ],
      confirm: false,
      timer: null,
      typy: t,
      info_msg: '',
      checkin_msg: '',
      error_msg: '',
      is_loading: false,
      td: true,
      facility: { id: null, },
      facility_name: '',
      facility_is_custom: false,
      localized_filters: [],
      form_language: '',
      phone_raw: '',
      phone_country_code: '',
      phone_number: '',
      checkin_date: '',
      questions: [],
      booking_details: {
        data: [],
        total: 0,
      },
      booking_details_fetched: [],
      search_name_matches: [],
      user: { email: '', },
      visitor: {
        visitor_id: null,
        first_name: null,
        last_name: null,
        address: null,
        phone_number: null,
        nationality: null,
      },
      visitor_forms: {},
      companions: [],
      currency: '',
      reservation_no: '',
      specials, different, canBeCopy, sameToAll, yesNo, choices2,
      form_choice_temp: {
        ids: {},
      },
      multiple_data: [],
      checkin_form_type: '',
      guestCompanions: null,
      custom_text_setting: { header: 'Text Header', },
      images: [],
      guestCount: 1,
      guest_name: '',
      vaccine_card_images: [],
      gov_id_exist: true,
      gov_id_companion: {
        exist: true,
      },
      vaccine_card_exist: true,
      vaccine_card_companion: {
        exist: true,
      },
      signature_exist: true,
      signature_written: false,
      reservation_no: null,
      reservation_no_query: null,
      showPaymentGateway: false,
      terms_field_id: null,
      agree_to_terms: true,
      //meal reservation details
      mealDetails: {},
      mealSelectedSlots: {},
      mealBooking: {},
      facilityInfo: [],
      mealRooms: [],
      hasAvailableTables: true,
      showMealError: false
    }
  },
  computed: {
    guestIsPaid() {
      const paidStatuses = [2,3,7,8,10,11];
      /*
      "pending" => 1,
      "paid" => 2,
      "refund" => 3,
      "paid_authorized" => 7,
      "paid_captured" => 8,
      "pay_at_reception" => 9,
      "credit_card_deposit" => 10,
      "paid_after_credit_card_deposit" => 11,
      "cancelled" => 6
      */
      if (this.booking_details.data.length < 1) return false;
      return paidStatuses.includes(this.booking_details.data[0].payment_status_id);
    },
      ...mapState('memorizedInputs', ['memorizedEmail', 'checkinFormType']),
      ...mapState("guest", [
          "numberOfGuest",
          "guestCountSetting",
          "fullName",
          "pass",
          "pmsCode",
          "userRemarks",
          "rsvNo",
          "roomNo"
      ])
  },
  async mounted() {
    this.form_language = this.$i18n.locale;
    const dateNow = moment().format('YYYY/MM/DD');
    this.checkin_date = dateNow;
    await this.fetchFacilityData();
    this.loadLocalizedFilter();
    this.setFacilityName();
    this.getFixedGuestCountSetting(this.facility.id);
    await this.fetchFacilityInMrs(this.facility.id)
    this.getCurrency();

    eventBus.$on('eventBusAddCompanion', (clickEvent) => {
      this.addNewCompanionEventBus(clickEvent);
    });
    eventBus.$on('eventBusRemoveCompanion', (companionId) => {
      this.removeCompanionEventBus(companionId)
    });
  },
  methods: {
    ...mapActions('memorizedInputs', ["saveCheckinFormType"]),
    ...mapActions('guest', ['getGuestCount', "checkNextType", "saveFullName"]),
    getChoice,
    formatDate,
    loadAgeDropdown,

     // call map room label api
    async mapRoomLabel(facilityId) {

      const payload = {
        "reservation_no": this.rsvNo,
         "pms_code": this.pmsCode,
         "pass": this.pass,
         "checkin_facility_id": facilityId
      }

      const response = await this.$lionheart.post('/meal-reservation/room-map-label', payload)
  
      let roomNumberRaw = response.data?.room_number ?? "";

      roomNumberRaw = String(roomNumberRaw).trim();
      let roomNumbers = [];

      if(roomNumberRaw) {
        roomNumbers = roomNumberRaw
        .split(/[,|]/)
        .map(r => r.trim())
        .filter(r => r !== "");
      }
       // handles the on prem facilities set room no
      if (roomNumbers.length === 0 && this.roomNo) {
        roomNumbers = this.roomNo;
      }

      this.mealRooms = roomNumbers;
      console.log('normalized room', this.mealRooms)
    },

    async fetchFacilityInMrs(facilityId) {
      try {
        const response = await this.$mrsApi.get(`/facility?checkin_facility_id=${facilityId}`);
        this.facilityInfo = response.data;
      } catch (error) {
        console.error('Error fetching facility info:', error);
      }
    },
    async fetchFacilityData() {
      this.facility = await this.$lionheart
        .post("/facility/list", { slug_name: this.$route.params.slug_name })
        .then((r) => this.catcher(r.data, "data[0].id", (r) => r.data[0]))
        .catch((e) => this.catcher(e));

      // Set the flag if the facility is customized
      this.facility_is_custom = this.facility.facility_setting.length > 0 ? true : false;

      if (this.facility.error.has_error) return this.$router.push("/404");
    },
    async validateEmail() {
      const data = {
        email: this.user.email,
        id: this.visitor.visitor_id,
        type: "visitor",
        module: "checkin",
      };
      const email = await this.$lionheart
        .post("/user/exists", data)
        .then((r) => this.catcher(r.data, "data.exist", (r) => r.data))
        .catch((e) => this.catcher(e));
      if (email.error.has_error) return this.errorDisplay(email);
      this.email_exists = email.exist;
      this.$root.$emit('email_exists', email.exist); //Check email component
    },
    selectLang(locale) {
      this.$i18n.locale = locale;
      this.form_language = locale;
      if (this.action == 'rsv_to_ci_select_filter') this.loadQuestions();
      this.loadCustomTextSetting(locale, this.facility.id);
      this.setFacilityName();
    },
    setFacilityName() {
      let locale = this.$i18n.locale;
      let facilityNames = {};
      let facilityName = '';

      this.facility.facility_name.forEach(el => {
        facilityNames[el.language] = el.name;
      });

      if (locale in facilityNames) {
        facilityName = facilityNames[locale];
      } else if ('en' in facilityNames) {
        facilityName = facilityNames.en;
      }

      if (facilityName.length == 0) facilityName = this.facility.name;

      this.facility_name = facilityName;
    },
    initializeAgreementStatus() {
      for (let question of this.questions) {
        if (question.system_name == 'terms_conditions') {
          this.terms_field_id = question.id;
          this.agree_to_terms = false;
          break;
        }
      }
    },
    setVisitorImage(images) {
      this.images = images;
    },
    async loadQuestions() {
      this.questions = await this.$lionheart
          .post("/facility-question/list", {
            facility_id: this.facility.id,
            language: this.$i18n.locale,
          })
          .then((r) => r.data.data);
    },
    phoneCountryChange(v) {
      this.phone_country_code = v.dialCode;
    },
    phoneInput(v, o) {
      // Get phone number only (excluding country code)
      this.phone_number = o.number.significant;
    },
    retakeImage(obj) {
      this.images[obj.index].dataUrl = obj.dataUrl;
    },
    retakeGovId(image) {
      if (image.index === 0) {
        this.visitor_forms[image.question_id] = {
          dataUrl: image.dataUrl,
        };
      } else {
        // if image.index is 0, it's for tour leader. Thus the minus one in index
        this.companions[image.index - 1].other_forms[image.question_id] = {
          dataUrl: image.dataUrl,
        };
      }
    },
    async getCurrency() {
      try {
        const { data } = await this.$lionheart.get(
          `facility/${this.facility.id}/currency`
        );
        this.currency = data.value;
      } catch (error) {
        this.currency = "JPY";
        console.error(error.response.data.message);
      }

      this.showPaymentGateway = true;
    },
    async populate(obj) {
      obj.forms.forEach(
        (obj) => this.$set(this.visitor_forms, obj.id, obj.answer),
        this
      );
      this.user.email = obj.email;
      await this.validateEmail();
    },
    saveImages(obj, moveToTopActivated = false) {
      this.visitor_forms = obj.visitor_forms;
      this.companions = obj.companions;
      this.gov_id_exist = true;
      this.gov_id_companion.exist = true;
      // if (moveToTopActivated && this.companions.length > 0) this.$refs.moveToTop.scrollIntoView({ behavior: 'smooth' });
    },
    async loadCustomTextSetting(lang, id, step='rsv_to_ci_select_filter') {
      let params;
      let response;
      this.custom_text_setting.title = '';
      this.custom_text_setting.header = '';

      if (step == 'rsv_to_ci_select_filter') { // Messages for localized filter
        params = {
          name: `message-checkin-filter-form-${lang}`,
          fallback: "message-checkin-filter-form-en",
        };
      }
      else { // Messages for checkin
        params = {
          name: `message-checkin-form-${lang}`,
          fallback: "message-checkin-form-en",
        };
      }

      response = await this.$lionheart.get("/custom-messages/" + id, {
        params: params,
      }).then(r => r.data.data);

      this.custom_text_setting = response.value ?? this.custom_text_setting;  //default value
    },
    async getFixedGuestCountSetting(facilityId) {
      try {
        const response = await this.$lionheart.get("/v2/facility-settings/fix-guest-count", { 
          params: {
            facility_id: facilityId
          }
        })

        this.getGuestCount(response.data.data)

      } catch(error) {
        console.error('Error fetching fixed guest count setting:', error);
      }
    },
    addNewCompanionEventBus(clickEvent) {
      this.$refs.defaultComponent[0].addCompanion(clickEvent);
      this.guestCount = this.images.length;
    },
    removeCompanionEventBus(companionIndex) {
      this.$refs.defaultComponent[0].removeCompanion(companionIndex);
      this.guestCount = this.images.length;
    },
    handleRemoveCompanionUsingGovId(companionIndex) {
      this.$refs.defaultComponent[0].removeCompanion(companionIndex);
      this.images.splice(companionIndex + 1, 1)
      this.guestCount = this.images.length;
    },
    hasPassportImage(obj) {
      let question = this.questions.find(el => el.system_name == 'gov_id' && el.required == 1);
      return obj.hasOwnProperty(question.id);
    },
    async validateFormAnswers() {
      // Passport image validation
      if (this.questions.find((el) => el.system_name == "gov_id" && el.required == 1)) {
        this.gov_id_companion.exist = true;
        this.gov_id_exist = true;
        
        if (!this.hasPassportImage(this.visitor_forms)) {
          this.gov_id_exist = false;
          console.warn('required gov_id for main guest is missing');
          return false;
        }

        this.companions.forEach((value, key) => {
          if (!this.hasPassportImage(value.other_forms)) {
            this.gov_id_companion = {
              exist: false,
              key: key,
            };
          }
        });

        if (!this.gov_id_companion.exist) {
          console.warn('required gov_id for companion guest is missing');
          return false;
        }
      }

      // Vaccine card validation
      if (this.questions.find((el) => el.system_name == "vaccine_card" && el.required == 1)) {
        this.vaccine_card_companion.exist = true;
        this.vaccine_card_exist = true;
        if (!this.hasVaccineCard(this.visitor_forms)) {
          this.vaccine_card_exist = false;
          console.warn('required vaccine_card for main guest is missing');
          return false;
        }

        this.companions.forEach((value, key) => {
          if (
            !this.hasVaccineCard(value.other_forms)
          ) {
            this.vaccine_card_companion = {
              exist: false,
              key: key,
            };
          }
        });

        if (!this.vaccine_card_companion.exist) {
          console.warn('required vaccine_card for companion guest is missing');
          return false;
        }
      }

      // Signature validation
      const sigElem = this.questions.find((el) => el.system_name == "signature");
      if (sigElem && sigElem.required == 1) {
        this.signature_exist = true;
        if (!this.hasSignature(this.visitor_forms)) {
          this.signature_exist = false;
          console.warn('required signature for main guest is missing');
          return false;
        }
      }

      console.log('image validation is clear');

      // Validate payment
      const paymentValid = await this.validatePayment();
      if (!paymentValid) return false;

      // To show confirmation alert
      if (this.booking_details.total > 1) {
        // To check if all forms are filled up
        const all_filled_up = this.multiple_data.every(function (data) {
          return data.filled_in_by_other == false
        })

        if (this.selected_option == 'separately' && !all_filled_up) {
          this.$bvModal.show('modal-confirmation-separately');
        }
      }

      return true;
    },
    async validatePayment() {
      
      if (!this.guestIsPaid) {

        // Validate Fincode
        const paymentComponent = this.$refs.gatewayPayment?.doCall?.();

        if (typeof paymentComponent?.validateBeforeConfirm === 'function') {
          paymentComponent.validateBeforeConfirm();
        }

        // Validate Stripe
        const status = await this.$refs.gatewayPayment.doCall().createPaymentMethod();
        if (!status) return status;
        this.error_msg = '';
        setTimeout(() => window.scrollTo(0, 10), 300);
      }
      return true;
    },
    setVisitorVaccineCardImages(images) {
      this.vaccine_card_images = images;
    },
    retakeVisitorVaccineCardImage(obj) {
      this.vaccine_card_images[obj.index].dataUrl = obj.dataUrl;
    },
    hasVaccineCard(obj) {
      let question = this.questions.find(el => el.system_name == 'vaccine_card' && el.required == 1);
      return obj.hasOwnProperty(question.id);
    },
    hasSignature(obj) {
      let question = this.questions.find(el => el.system_name == 'signature' && el.required == 1);
      return obj.hasOwnProperty(question.id);
    },
    clearSigError() {
      clearTimeout(this.timer);
      this.signature_exist = true;
      this.signature_written = false;
    },
    async loadLocalizedFilter() {
      // check if the facility is capable of filtering local or international
      const response = await this.$lionheart.post("/facility-setting/list", {
        facility_id: this.facility.id,
        name: "localized-filter",
        paginate: false,
      });

      if (response.data.total != 0) {
        if (response.data.data[0].value.visibility == true) {
          const lf = await this.$lionheart.get("localized-filters?facility_id=" + this.facility.id);
          if (lf.data.data.length > 0) {
            // this.is_localized = true;
            this.localized_filters = lf.data.data;
          }
        }
      }
      // Load the initial display
      // this.next =
      //   this.is_custom == true
      //     ? "pre-checkin-form"
      //     : this.is_localized == true
      //       ? "localized-filters"
      //       : "checkin-form";

    },
    async selectFilter(category, from) {
      // function to get all questions based on facilityId
      let questionsPayload = {
        facility_id: this.facility.id,
        language: this.$i18n.locale,
      };
      if (category != null) {
        this.selected_localized_category = category;
        questionsPayload.facilities_localized_category_id = category.id;
      }
      const response = await this.$lionheart.post("/facility-question/list", questionsPayload);
      if (response.data.total != 0) {
        this.questions = response.data.data;
        // if (!from) this.updateMultipleData();
      }
      this.initializeAgreementStatus();
      // trigger when selecting filter tag to get custom text setting
      await this.loadCustomTextSetting(this.$i18n.locale, this.facility.id, 'rsv_to_ci_form_input');
      this.action = 'rsv_to_ci_form_input';
      // fetch meal reservation details
      await this.checkAndFetchMeal();
    },
    setAgreementStatus(e) {
      this.agree_to_terms = e.target.checked ? true : false;
    },
    mealSelection(selections) {
      this.mealSelectedSlots = selections;
    },
    async checkAndFetchMeal() {
      const bookingDetails = this.booking_details.data ?? null;
      const reservationNo = bookingDetails[0]?.pms_reservation_no;
      const facilityId = this.facility?.id;
      const pass = bookingDetails[0]?.pass;
      
      if (facilityId && pass && reservationNo) {
        await this.fetchMealDetails(reservationNo, facilityId, pass);
      }
    },
    async fetchMealDetails(rsvNo, facilityId, pass) {
      const payload = { reservation_no: rsvNo, facility_id: facilityId, pass: pass };

      try {
        const response = await this.$lionheart.get('/meal-reservation/meal', {
          params: payload
        });
        this.mealDetails = response.data;
         this.mapRoomLabel(facilityId); 
      } catch (error) {
        console.error("Error fetching meal data:", error);
      }
    },

    errorDisplay(d) {
      this.error_msg = d.error.msg;
      setTimeout(
        () => window.scrollTo(0, $("#error_msg").position().top - 10),
        300
      );
      return;
    },
    async submitByPhone(e) {
      this.info_msg = '';
      this.error_msg = '';
      this.visitor_forms = {};

      const payload = {
        'facility_id': this.facility.id,
        'mode': 'phone',
        'search': `${this.phone_country_code}|${this.phone_number}`,
        'checkin_date': moment(this.checkin_date).format('YYYY-MM-DD')
      };

      const response = await this.$lionheart.post('/facility-booking/list', payload)
        .then((r) => r.data)
        .catch((e) => this.catcher(e));
      
      // Reset questionnaire form to input
      this.confirm = false;
      
      // Decide next action based on search result.
      if (response.total < 1) {
        // Stop if no reservation found
        this.error_msg = this.$t('checkin_by_phone.error_no_reservation');
      } else if (response.total > 1) {
        // Stop if multiple reservation is detected
        // this.error_msg = this.$t('checkin_by_phone.error_multiple_reservation');
        this.action = 'search_name';
        this.booking_details_fetched = response;
      } else if (response.data[0].booking_status_id == 2) {
        // Stop if guest has already checked in (status 2 = checked in)
        this.info_msg = this.$t('checkin_by_phone.info_already_checkedin');
      } else if (checkedOutStatusIds.includes(response.data[0].booking_status_id)) {
        // Stop if guest has already checked out (see checkedOutStatusIds above)
        this.error_msg = this.$t('checkin_by_phone.error_already_checkedout');
      } else {
        this.booking_details = response;
        if (response.data[0].booking_status_id == 13) {
          // Proceed from PCI to CI flow.
          this.action = 'pci_to_ci_form_input';
        } else {
          // Proceed from reserve to CI flow.
          if (this.localized_filters.length > 0) {
            this.action = 'rsv_to_ci_select_filter';
            await this.loadCustomTextSetting(this.$i18n.locale, this.facility.id, 'rsv_to_ci_select_filter');
          } else {
            this.selectFilter();
          }
        }
      }
    },
    async submitByGuestName(e) {
      const guestName = this.guest_name;
      const normalizeSearchableNameString = (str) => str.replace(/\s/g, '').toLowerCase();
      const matchByExactName = this.booking_details_fetched.data.filter(
        o => normalizeSearchableNameString(o.parsed.full_name) == normalizeSearchableNameString(guestName)
      );

      if (matchByExactName.length < 1) {
        // Stop if guest name input is a missmatch from cached list.
        this.error_msg = this.$t('checkin_by_phone.error_no_reservation');
      } else if (matchByExactName.length > 1) {
        // Stop if guest name input has multiple match found.
        this.error_msg = this.$t('checkin_by_phone.error_multiple_reservation');
      } else if (matchByExactName[0].booking_status_id == 2) {
        // Stop if guest has already checked in (status 2 = checked in)
        this.error_msg = this.$t('checkin_by_phone.info_already_checkedin');
      } else if (checkedOutStatusIds.includes(matchByExactName[0].booking_status_id)) {
        // Stop if guest has already checked out (see checkedOutStatusIds above)
        this.error_msg = this.$t('checkin_by_phone.error_already_checkedout');
      } else {
      
        this.booking_details.data = matchByExactName;
        this.booking_details.total = matchByExactName.length; // apply total in searchByGuestName

        if (matchByExactName[0].booking_status_id == 13) {
          // Proceed from PCI to CI flow.
          this.action = 'pci_to_ci_form_input';
        } else {
          // Proceed from reserve to CI flow.
          if (this.localized_filters.length > 0) {
            this.action = 'rsv_to_ci_select_filter';
            await this.loadCustomTextSetting(this.$i18n.locale, this.facility.id, 'rsv_to_ci_select_filter');
          } else {
            this.selectFilter();
          }
        }
      }
    },
    back() {
      // Clear messages.
      this.info_msg = '';
      this.error_msg = '';

      // Select action display
      switch (this.action) {
        case 'rsv_to_ci_form_confirm':
          this.action = 'rsv_to_ci_form_input';
          this.confirm = false;
          break;
        case 'rsv_to_ci_form_input':
          this.action = this.localized_filters.length > 0 ? 'rsv_to_ci_select_filter' : 'search_booking';
          break;
        case 'pci_to_ci_form_confirm':
          this.action = 'pci_to_ci_form_input';
          this.confirm = false;
          break;
        default:
          this.action = 'search_booking';
      }

      const beforePayment = ['rsv_to_ci_form_input', 'pci_to_ci_form_input'];

      if (beforePayment.includes(this.action)) {
        if (!this.guestIsPaid) setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
      }
    },
    async goCiCompletePage(email, pass) {
      const myPage = await this.$lionheart
        .get("/v2/facility-settings", {
          params: {
            name: 'my-page-setting',
            facility_id: this.facility.id,
            paginate: 0
          }
        })
        .then(res => res.data.data);

      let shareableLink = false;
      const queryParam = this.td ? { ref: "td" } : {};
      // this will check if the guest if from tablet mode
      if (this.$route.query.tab && this.$route.query.tab == 1) queryParam.tab = 1;

      let resultOfMyPage = myPage[0]?.value.active ?? false;

      let reservationNumberQuery = this.booking_details.data[0]?.pms_reservation_no ?? null
      // ONLY DISPLAY THE PASS, AS THERE IS A RULE THAT,
      // IF MYPAGE IS DISABLED, IT CAN'T BE ACCESSED
      if (resultOfMyPage) queryParam.email = email;
      queryParam.pass = pass;

      if (reservationNumberQuery) {
        queryParam.rsv_no = reservationNumberQuery;
        if(t(this.$route.query, 'tCode').isDefined) queryParam.tCode = this.$route.query.tCode;
      }

      // Show success page
      this.$bvModal.hide('processing-modal')
      this.$router.push({
        name: "checkin-facility-checkin-success",
        params: {
          slug_name: this.$route.params.slug_name,
          show_shareable_link: shareableLink, // This will pop up shareable link modal
          rsv_no: reservationNumberQuery,
          email,
          pass,
        },
        query: queryParam,
      });
    },
    async submitForm() {

        // error validation for meal forms
        if (this.mealDetails?.hasMealData && Object.keys(this.mealSelectedSlots).length !== 0 && this.facilityInfo[0]?.status == "enabled" && this.facilityInfo.length > 0) {
          const isFilled = this.$refs.mealComponent.validateSelections();
          if (!isFilled) return;
        }

       
        // MRS: availability checker
        if (this.mealDetails?.hasMealData && Object.keys(this.mealSelectedSlots).length !== 0 && this.facilityInfo[0]?.status == "enabled" && this.facilityInfo.length > 0) {
           const payload = {
            meal_details: this.mealSelectedSlots,
            reservation_no: this.rsvNo,
            facility_id: this.facility.id,
            status: "reserved",
            room: this.mealRooms,
            remarks: this.userRemarks,
            pms_code: this.pmsCode,
            pass: this.pass,
            is_checker: true
          }

          console.log('action', this.action);

           const checkMrsTableAvailability = await this.$mrsApi.post('/cis-reservation', payload)
            .then((m) => this.catcher(m, "data", (m) => m.data))
            .catch((m) => this.catcher(m));

          if (checkMrsTableAvailability.error.has_error) {
            return this.errorDisplay(checkMrsTableAvailability);
          }

           if (checkMrsTableAvailability.avaialble_tables === 0) {
            // update the hasAvailableTables in meal compomnent
            this.hasAvailableTables = false;
            this.showMealError = true;
          }

          if (!this.hasAvailableTables) {
            if (!this.$refs.mealComponent.noMealConfirmed) {
              
                if (this.action === 'rsv_to_ci_form_confirm') {
                  this.action = 'rsv_to_ci_form_input';
                  this.confirm = false;
                  // reinit payment 
                  this.reInitPayment();

                } else if (this.action === 'pci_to_ci_form_confirm') {
                  this.action = 'pci_to_ci_form_input';
                   this.reInitPayment();
                  this.confirm = false;
                } else if (this.action === 'rsv_to_ci_form_input') {
                  this.confirm = false;
                  return
                }
            } else {
              this.showMealError = false;
            }
          }
        }        
 

      if (this.action == 'rsv_to_ci_form_input') {

        let isFormValid = await this.validateFormAnswers();
        if (isFormValid) {
          this.action = 'rsv_to_ci_form_confirm';
          this.confirm = true;
        }
        return false;
      } else if (this.action == 'rsv_to_ci_form_confirm') {
        await this.rsvToCi();
      }
      else if (this.action == 'pci_to_ci_form_input') {
        await this.pciToCi();
      }
    },
    
    reInitPayment() {
       const paymentComponent = this.$refs.gatewayPayment?.doCall?.();

        if (typeof paymentComponent?.turnoffAlert === 'function') {
          paymentComponent.turnoffAlert();
        }

        setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
        window.scrollTo(0, 10);
    },


    searchNames(e) {
      // Clear messages.
      this.info_msg = '';
      this.error_msg = '';

      // Update search result.
      let guestName = this.guest_name;
      let searchNameMatches = this.booking_details_fetched.data.filter(function(o) {
        var re = new RegExp(guestName);
        return o.parsed.full_name.search(re) > -1;
      });

      this.search_name_matches = searchNameMatches;
    },
    
    reInitPayment() {
       const paymentComponent = this.$refs.gatewayPayment?.doCall?.();

        if (typeof paymentComponent?.turnoffAlert === 'function') {
          paymentComponent.turnoffAlert();
        }

        setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
        window.scrollTo(0, 10);
    },

    searchNames(e) {
      // Clear messages.
      this.info_msg = '';
      this.error_msg = '';

      // Update search result.
      let guestName = this.guest_name;
      let searchNameMatches = this.booking_details_fetched.data.filter(function(o) {
        var re = new RegExp(guestName);
        return o.parsed.full_name.search(re) > -1;
      });

      this.search_name_matches = searchNameMatches;
    },
    
    reInitPayment() {
       const paymentComponent = this.$refs.gatewayPayment?.doCall?.();

        if (typeof paymentComponent?.turnoffAlert === 'function') {
          paymentComponent.turnoffAlert();
        }

        setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
        window.scrollTo(0, 10);
    },

    searchNames(e) {
      // Clear messages.
      this.info_msg = '';
      this.error_msg = '';

      // Update search result.
      let guestName = this.guest_name;
      let searchNameMatches = this.booking_details_fetched.data.filter(function(o) {
        var re = new RegExp(guestName);
        return o.parsed.full_name.search(re) > -1;
      });

      this.search_name_matches = searchNameMatches;
    },
    async pciToCi() {

      // Validate payment
      const paymentValid = await this.validatePayment();
      if (!paymentValid) return false;

      this.action = 'pci_to_ci_form_confirm';
      this.confirm = true;

      // Submit payment
      if (!this.guestIsPaid) {
        const payment = await this.$refs.gatewayPayment.doCall().makePayment(true);
        if (!payment) {
         this.confirm = false;
         setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
         return;
        };
      }

      // API call to complete checkin
      const bookingData = this.booking_details.data[0];
      const payload = {
        pass: bookingData.pass,
        pms_code: bookingData.pms_code,
      }
      const checkin = await this.$lionheart
        .post("/facility-booking/finish-precheckin", payload, this.$bvModal.show('processing-modal'))
        .then((r) => this.catcher(r.data, "success", (r) => r.success))
        .catch((e) => this.catcher(e));

      if (checkin.error.has_error) {
        this.error_msg = error.msg;
        return;
      }
      
      // Show CI completion page
      await this.goCiCompletePage(bookingData.parsed.email_address ?? '', bookingData.pass);
    },
    async rsvToCi() {
      this.noQuestions = true;

      const visitor = this.visitor;
      const user = this.user;

      let visitor_forms = _.cloneDeep(this.visitor_forms);
      let companions = _.cloneDeep(this.companions);

      const dobObj = this.questions.find((obj) => obj.system_name == "dob");
      const govidObj = this.questions.find((obj) => obj.system_name == "gov_id");
      const vaccineCardObj = this.questions.find((obj) => obj.system_name == "vaccine_card");
      let booking_details = this.booking_details.data;

      if (dobObj) {
        visitor_forms[dobObj.id] = moment(this.formatDate(visitor_forms[dobObj.id])).format("YYYY-MM-DD");
      }

      if (govidObj) {
        if (visitor_forms[govidObj.id]) visitor_forms[govidObj.id] = visitor_forms[govidObj.id].dataUrl;
      }

      if (vaccineCardObj) {
        if (visitor_forms[vaccineCardObj.id]) {
          visitor_forms[vaccineCardObj.id] = visitor_forms[vaccineCardObj.id].dataUrl;
        }
      }

      companions = companions.map((obj) => {
        Object.entries(obj.other_forms).map((oc) => oc[1] == null || undefined ? delete obj.other_forms[oc[0]] : '');
        obj.other_forms = Object.entries(obj.other_forms).reduce(
          (a, [k, v]) => (v == '' ? a : ((a[k] = v), a)),
          {}
        );

        if (dobObj) {
          obj.other_forms[dobObj.id] = moment(this.formatDate(obj.other_forms[dobObj.id])).format("YYYY-MM-DD");
        }

        if (govidObj) {
          if (obj.other_forms[govidObj.id]) {
            obj.other_forms[govidObj.id] = obj.other_forms[govidObj.id].dataUrl;
          }
        }

        if (vaccineCardObj) {
          if (obj.other_forms[vaccineCardObj.id]) {
            obj.other_forms[vaccineCardObj.id] = obj.other_forms[vaccineCardObj.id].dataUrl;
          }
        }

        return obj;
      });

      visitor_forms = Object.entries(visitor_forms).reduce((a, [k, v]) => (v == '' ? a : ((a[k] = v), a)),{});
      // this.hideButton = true; // this will prevent the duplication of payment esp. fincode
    
      // do this when submitting form (going to do 3d and checkin) then if error go back to input cards
      if (!this.guestIsPaid) {
        const payment = await this.$refs.gatewayPayment.doCall().makePayment(true);
        if (!payment) {
          setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
          return;
        };
      }

      let data = [];

      if (this.booking_details.total > 1) {
        this.multiple_data.forEach((item, index) => {
          if (item.filled_in_by_other == false) {
            const booking = [];
            booking.push(booking_details[index]);

            if (govidObj) {
              if (govidObj.id in item.visitor_forms) {
                item.visitor_forms[govidObj.id] = item.visitor_forms[govidObj.id].dataUrl;
              }
              item.companions.forEach((item, key) => {
                if (govidObj.id in item.other_forms) {
                  item.other_forms[govidObj.id] = item.other_forms[govidObj.id].dataUrl;
                }
              });
            }

            if (vaccineCardObj) {
              if (vaccineCardObj.id in item.visitor_forms) {
                item.visitor_forms[vaccineCardObj.id] = item.visitor_forms[vaccineCardObj.id].dataUrl;
              }
              item.companions.forEach((item, key) => {
                if (vaccineCardObj.id in item.other_forms) {
                  item.other_forms[vaccineCardObj.id] = item.other_forms[vaccineCardObj.id].dataUrl;
                }
              });
            }

            data.push({
              facility_id: this.facility.id,
              timezone: moment.tz.guess(),
              companions: item.companions,
              ...visitor,
              ...user,
              email: item.email,
              other_form: item.visitor_forms,
              booking_details: booking,
              td: this.td,
            });
          }
        });

        // To check if all forms are filled up
        const allFilledUp = this.multiple_data.every((data) => data.filled_in_by_other == false);

        if (this.selected_option == 'separately' && !allFilledUp) shareableLink = true;
      } else {
        // Normal checkin
        if(this.memorizedEmail) user['email'] = this.memorizedEmail;

        data = [
          {
            facility_id: this.facility.id,
            timezone: moment.tz.guess(),
            companions,
            ...visitor,
            ...user,
            other_form: visitor_forms,
            booking_details,
            td: 1,
          },
        ];
      }

    //  console.log('form submit data', data)  // checkin incoming data
    //  console.log('meal form submit', this.mealSelectedSlots) // meal incoming selected data 

  if (this.mealDetails?.hasMealData && Object.keys(this.mealSelectedSlots).length !== 0 && this.facilityInfo[0]?.status == "enabled" && this.facilityInfo.length > 0) {
      const nameObj = this.questions.find((obj) => obj.system_name === "full_name");
      this.saveFullName(visitor_forms[nameObj.id]);

      const payload = {
        meal_details: this.mealSelectedSlots,
        reservation_no: this.rsvNo,
        facility_booking_id: null,
        facility_id: this.facility.id,
        status: "reserved",
        name: this.fullName,
        room: this.mealRooms,
        remarks: this.userRemarks,
        pms_code: this.pmsCode,
        pass: this.pass
      };

      console.log("payload", payload);

      let mrsCheckin;
      try {
        const response = await this.$mrsApi.post('/cis-reservation', payload, this.$bvModal.show('processing-modal'));
        mrsCheckin = this.catcher(response, "data", (r) => r.data);
      } catch (err) {
        mrsCheckin = this.catcher(err);
      }

      if (mrsCheckin.error.has_error) {
        this.$bvModal.hide('processing-modal');
        return this.errorDisplay(mrsCheckin);
      }

      let checkinSubmit;
      try {
        checkinSubmit = await this.checkinSubmit(data);
      } catch (e) {
        checkinSubmit = this.catcher(e);
      }

      if (checkinSubmit.error?.has_error) {
        try {
          await this.$mrsApi.delete('/reservations/cis-delete', {
            data: {
              reservation_no: this.rsvNo,
              pass: this.pass,
              pms_code: this.pmsCode
            }
          });
        } catch (deleteErr) {
          console.error('Delete MRS reservations failed:', deleteErr?.response?.data?.message || deleteErr.message);
          this.catcher(deleteErr);
        }

        this.$bvModal.hide('processing-modal');
        return this.errorDisplay(checkinSubmit);
      }

      // Proceed to update the facility_booking_id
      try {
        const params = {
          reservation_no: this.rsvNo,
          pms_code: this.pmsCode,
          pass: this.pass,
        };

        const response = await this.$lionheart.get('/meal-reservation/booking-id', { params });
        const checkinBooking = this.catcher(response, "data", (r) => r.data);

        if (checkinBooking?.data?.id) {
          const mrsUpdatePayload = {
            reservation_no: this.rsvNo,
            facility_booking_id: checkinBooking.data.id,
            pms_code: this.pmsCode,
            pass: this.pass
          };

          await this.$mrsApi.patch('/reservations/update-facility-booking-id', mrsUpdatePayload)
            .then((res) => this.catcher(res, "data", (r) => r.data))
            .catch((err) => this.catcher(err));
        }
      } catch (e) {
        console.error('Booking ID update failed:', e);
      }

      if (!checkinSubmit[0]?.booking) {
        this.$bvModal.hide('processing-modal');
        setTimeout(() => window.scrollTo(0, $("#checkin_msg").position().top - 10), 300);
        return (this.checkin_msg = true);
      }

      this.error_msg = "";

      let queryEmail = '';
      let queryPass = '';

      if (checkinSubmit[0]?.custom === true) {
        queryEmail = this.typy(this.booking_details, 'data[0].parsed.email_address').isDefined
          ? this.booking_details.data[0].parsed.email_address
          : data[0]?.email;
        queryPass = this.typy(this.booking_details, 'data[0].pass').isDefined
          ? this.booking_details.data[0].pass
          : checkinSubmit[0].bookingDetails.pass;
      }

      await this.goCiCompletePage(queryEmail, queryPass);
    } else {
      // REGULAR CHECKIN if no meal reservation
      const checkin = await this.checkinSubmit(data);

      if (checkin.error?.has_error) {
        this.$bvModal.hide('processing-modal');
        return this.errorDisplay(checkin);
      }

      if (!checkin[0]?.booking) {
        this.$bvModal.hide('processing-modal');
        setTimeout(() => window.scrollTo(0, $("#checkin_msg").position().top - 10), 300);
        return (this.checkin_msg = true);
      }

      this.error_msg = "";

      let queryEmail = '';
      let queryPass = '';

      if (checkin[0]?.custom === true) {
        queryEmail = this.typy(this.booking_details, 'data[0].parsed.email_address').isDefined
          ? this.booking_details.data[0].parsed.email_address
          : data[0]?.email;
        queryPass = this.typy(this.booking_details, 'data[0].pass').isDefined
          ? this.booking_details.data[0].pass
          : checkin[0].bookingDetails.pass;
      }

      await this.goCiCompletePage(queryEmail, queryPass);
    }
    },
     // checkin submission method
    async checkinSubmit(data) {
      const checkin = await this.$lionheart
        .post("/checkin", data, this.$bvModal.show('processing-modal'))
        .then((r) => {
          return this.catcher(r.data, "status", (r) => r.status);
        })
        .catch((e) => this.catcher(e));

      return checkin;
    },
  },
  watch: {
    signature_exist(yes) {
      if (!yes) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.signature_exist = true; }, 4000);
      }
    },
    facility: {
      handler() {
        this.checkAndFetchMeal();
      },
      deep: true
    },
    reservation_no() {
      this.checkAndFetchMeal();
    },
    rsvNo() {
       this.checkAndFetchMeal();
    },
    pmsCode() {
      this.checkAndFetchMeal();
    },
    pass() {
      this.checkAndFetchMeal();
    },
    guestCountSetting() { },
    numberOfGuest(val) {
      if (val > 0) {
        this.guestCompanions = val;
      }
    },
  }
}
</script>